import { formatEuroFr } from './editProductOutcome.js'

export const ANALYTICS_PERIODS = [
  { id: 'all', label: 'Tous les paniers' },
  { id: 'year', label: '12 derniers mois' },
  { id: '90d', label: '90 jours' },
  { id: '30d', label: '30 jours' },
]

export const ANALYTICS_SLIDER_PERIODS = [
  { id: '30d', label: '30 jours', short: '30 j' },
  { id: '90d', label: '90 jours', short: '90 j' },
  { id: 'year', label: '12 derniers mois', short: '12 mois' },
  { id: 'all', label: 'Tous les paniers', short: 'Tous' },
]

export const ANALYTICS_PERIOD_STORAGE = 'camit.analytics.period'
export const ANALYTICS_SCROLL_STORAGE = 'camit.analytics.scroll'
export const ANALYTICS_RETURN_STORAGE = 'camit.analytics.return'

export function analyticsPeriodIndexFromId(id) {
  const index = ANALYTICS_SLIDER_PERIODS.findIndex((item) => item.id === id)
  return index >= 0 ? index : ANALYTICS_SLIDER_PERIODS.length - 1
}

export function analyticsPeriodIdFromIndex(index) {
  const last = ANALYTICS_SLIDER_PERIODS.length - 1
  const next = Math.max(0, Math.min(last, Number.parseInt(index, 10) || 0))
  return ANALYTICS_SLIDER_PERIODS[next].id
}

function storageGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function storageSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* ignore quota / private mode */
  }
}

function storageRemove(key) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

export function readStoredAnalyticsPeriod() {
  const value = storageGet(ANALYTICS_PERIOD_STORAGE)
  return ANALYTICS_SLIDER_PERIODS.some((item) => item.id === value) ? value : 'all'
}

export function writeStoredAnalyticsPeriod(period) {
  if (ANALYTICS_SLIDER_PERIODS.some((item) => item.id === period)) {
    storageSet(ANALYTICS_PERIOD_STORAGE, period)
  }
}

export function captureAnalyticsScroll(y = typeof window === 'undefined' ? 0 : window.scrollY) {
  storageSet(ANALYTICS_SCROLL_STORAGE, String(Math.max(0, Number(y) || 0)))
}

export function consumeAnalyticsScroll() {
  const raw = storageGet(ANALYTICS_SCROLL_STORAGE)
  storageRemove(ANALYTICS_SCROLL_STORAGE)
  const next = Number(raw)
  return Number.isFinite(next) ? next : null
}

export function markAnalyticsReturn() {
  captureAnalyticsScroll()
  storageSet(ANALYTICS_RETURN_STORAGE, '1')
}

export function peekAnalyticsReturn() {
  return storageGet(ANALYTICS_RETURN_STORAGE) === '1'
}

export function consumeAnalyticsReturn() {
  const flagged = peekAnalyticsReturn()
  storageRemove(ANALYTICS_RETURN_STORAGE)
  return flagged
}

export function shouldNavigateAnalyticsRow({
  hasId,
  isInteractiveTarget,
  hasTextSelection,
  pointerMoved,
} = {}) {
  if (!hasId) return false
  if (isInteractiveTarget) return false
  if (hasTextSelection) return false
  if (pointerMoved) return false
  return true
}

export const ANALYTICS_SERIES = {
  dark: ['#8fb4ff', '#6ec8b8', '#d7b15c', '#c792ea', '#f08a6b', '#9bb7d4'],
  light: ['#3d6fd9', '#2a9d8f', '#9a7200', '#6b4ea0', '#c45c3e', '#4d6a88'],
}

const PRICE_RANGES = [
  { label: '0-10 €', min: 0, max: 10 },
  { label: '10-25 €', min: 10, max: 25 },
  { label: '25-50 €', min: 25, max: 50 },
  { label: '50-100 €', min: 50, max: 100 },
  { label: '100-200 €', min: 100, max: 200 },
  { label: '200 € et plus', min: 200, max: Infinity },
]

export function analyticsSeriesColors(theme) {
  return theme === 'light' ? ANALYTICS_SERIES.light : ANALYTICS_SERIES.dark
}

export function formatDateFr(value, options) {
  if (!value) return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('fr-FR', options || {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function formatMonthFr(value) {
  if (!value) return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Axes verticaux sur écran étroit : une graduation doit tenir dans ~48 px.
export function formatEuroAxisCompact(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ''
  const abs = Math.abs(numeric)
  const sign = numeric < 0 ? '-' : ''
  if (abs >= 1000000) {
    const millions = abs / 1000000
    return `${sign}${millions.toLocaleString('fr-FR', { maximumFractionDigits: abs >= 10000000 ? 0 : 1 })}\u00a0M€`
  }
  if (abs >= 1000) {
    const thousands = abs / 1000
    return `${sign}${thousands.toLocaleString('fr-FR', { maximumFractionDigits: abs >= 10000 ? 0 : 1 })}\u00a0k€`
  }
  return `${sign}${abs.toLocaleString('fr-FR', { maximumFractionDigits: abs >= 10 ? 0 : 2 })}\u00a0€`
}

export function truncateLabel(value, max = 28) {
  const text = String(value || '').trim()
  if (text.length <= max) return text
  return `${text.slice(0, Math.max(0, max - 1))}…`
}

export function filterCartsByPeriod(carts, period, now = new Date()) {
  const list = Array.isArray(carts) ? carts : []
  if (!period || period === 'all') return list
  const days = period === '30d' ? 30 : period === '90d' ? 90 : period === 'year' ? 365 : null
  if (!days) return list
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000
  return list.filter((cart) => {
    if (!cart?.created_at) return false
    const time = new Date(cart.created_at).getTime()
    return Number.isFinite(time) && time >= cutoff
  })
}

function cartValue(cart) {
  return Number(cart?.total) || 0
}

function cartBudget(cart) {
  return Number(cart?.budget) || 0
}

export function buildAnalyticsModel(carts, items, { period = 'all', now = new Date() } = {}) {
  const scopedCarts = filterCartsByPeriod(carts, period, now)
  const scopedIds = new Set(scopedCarts.map((cart) => cart.id).filter(Boolean))
  const scopedItems = (Array.isArray(items) ? items : []).filter((item) => scopedIds.has(item.cart_id))

  const totalCarts = scopedCarts.length
  const cartValueTotal = scopedCarts.reduce((sum, cart) => sum + cartValue(cart), 0)
  const withBudget = scopedCarts.filter((cart) => cartBudget(cart) > 0)
  const withoutBudget = totalCarts - withBudget.length
  const budgetTotal = withBudget.reduce((sum, cart) => sum + cartBudget(cart), 0)
  const valueWithBudget = withBudget.reduce((sum, cart) => sum + cartValue(cart), 0)
  const budgetUtilization = budgetTotal > 0 ? (valueWithBudget / budgetTotal) * 100 : null
  const averageCartValue = totalCarts > 0 ? cartValueTotal / totalCarts : 0

  const cartRows = [...scopedCarts]
    .map((cart) => ({
      id: cart.id,
      name: truncateLabel(cart.name, 22),
      fullName: cart.name || 'Sans nom',
      total: cartValue(cart),
      budget: cartBudget(cart),
      gap: cartBudget(cart) > 0 ? cartBudget(cart) - cartValue(cart) : null,
      date: formatDateFr(cart.created_at),
      updated: formatDateFr(cart.updated_at),
    }))
    .sort((a, b) => b.total - a.total)

  const timeSeries = [...scopedCarts]
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
    .reduce((rows, cart) => {
      const previous = rows.length ? rows[rows.length - 1].cumulative : 0
      const total = cartValue(cart)
      rows.push({
        date: formatDateFr(cart.created_at, { day: 'numeric', month: 'short' }),
        fullDate: formatDateFr(cart.created_at),
        name: cart.name || 'Sans nom',
        total,
        cumulative: previous + total,
      })
      return rows
    }, [])

  const budgetComparison = withBudget
    .map((cart) => ({
      name: truncateLabel(cart.name, 22),
      fullName: cart.name || 'Sans nom',
      budget: cartBudget(cart),
      valeur: cartValue(cart),
      écart: cartBudget(cart) - cartValue(cart),
    }))
    .sort((a, b) => b.budget - a.budget)

  const ranked = [...cartRows]
  const pieData = ranked.slice(0, 5).map((cart) => ({
    name: cart.name,
    fullName: cart.fullName,
    value: cart.total,
  }))
  const othersTotal = ranked.slice(5).reduce((sum, cart) => sum + cart.total, 0)
  if (othersTotal > 0) {
    pieData.push({ name: 'Autres', fullName: 'Autres paniers', value: othersTotal })
  }

  const monthlyMap = {}
  scopedCarts.forEach((cart) => {
    if (!cart.created_at) return
    const date = new Date(cart.created_at)
    if (Number.isNaN(date.getTime())) return
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    if (!monthlyMap[monthKey]) {
      monthlyMap[monthKey] = { month: formatMonthFr(date), monthKey, total: 0, count: 0 }
    }
    monthlyMap[monthKey].total += cartValue(cart)
    monthlyMap[monthKey].count += 1
  })
  const monthlyData = Object.values(monthlyMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey))

  const productFrequency = {}
  scopedItems.forEach((item) => {
    const name = item.name || 'Produit sans nom'
    if (!productFrequency[name]) {
      productFrequency[name] = {
        name,
        count: 0,
        units: 0,
        totalPrice: 0,
        carts: new Set(),
      }
    }
    const quantity = Number(item.quantity) || 0
    productFrequency[name].count += 1
    productFrequency[name].units += quantity
    productFrequency[name].totalPrice += Number(item.price) || 0
    if (item.cart_name) productFrequency[name].carts.add(item.cart_name)
  })

  const mostFrequentProducts = Object.values(productFrequency)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map((product) => ({
      name: truncateLabel(product.name, 28),
      fullName: product.name,
      count: product.count,
      units: product.units,
      averagePrice: product.count ? product.totalPrice / product.count : 0,
      cartsCount: product.carts.size,
    }))

  const topExpensiveProducts = scopedItems
    .filter((item) => Number(item.price) > 0)
    .map((item) => ({
      name: truncateLabel(item.name || 'Produit sans nom', 28),
      fullName: item.name || 'Produit sans nom',
      price: Number(item.price) || 0,
      cart_name: item.cart_name || 'Inconnu',
    }))
    .sort((a, b) => b.price - a.price)
    .slice(0, 10)

  const prices = scopedItems
    .map((item) => Number(item.price) || 0)
    .filter((price) => price > 0)
  const averagePrice = prices.length ? prices.reduce((sum, price) => sum + price, 0) / prices.length : 0
  const minPrice = prices.length ? Math.min(...prices) : 0
  const maxPrice = prices.length ? Math.max(...prices) : 0
  const priceDistribution = PRICE_RANGES
    .map((range) => ({
      ...range,
      count: prices.filter((price) => price >= range.min && price < range.max).length,
    }))
    .filter((range) => range.count > 0)

  const references = scopedItems.length
  const units = scopedItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)

  return {
    period,
    totalCarts,
    cartValueTotal,
    averageCartValue,
    budgetTotal,
    valueWithBudget,
    budgetUtilization,
    cartsWithBudget: withBudget.length,
    cartsWithoutBudget: withoutBudget,
    cartRows,
    timeSeries,
    budgetComparison,
    pieData,
    monthlyData,
    mostFrequentProducts,
    topExpensiveProducts,
    priceDistribution,
    references,
    units,
    uniqueProducts: Object.keys(productFrequency).length,
    averagePrice,
    minPrice,
    maxPrice,
    labels: {
      cartValue: 'Valeur des paniers',
      average: 'Moyenne par panier',
      utilization: 'Utilisation du budget',
      utilizationHint: withBudget.length
        ? 'Rapport valeur / budget, uniquement pour les paniers qui ont un budget.'
        : 'Aucun panier n’a de budget défini.',
      timeSeries: 'Valeur cumulée des paniers',
      timeSeriesHint: 'Cumul des valeurs actuelles, classé par date de création. Ce n’est pas un historique de dépenses.',
      monthly: 'Valeur des paniers créés par mois',
      budget: 'Budget et valeur actuelle',
      distribution: 'Répartition de la valeur',
      references: 'Références',
      units: 'Unités',
    },
    format: {
      money: formatEuroFr,
      date: formatDateFr,
    },
  }
}

export function chartDetailsFor(model, chartType) {
  if (!model) return null
  switch (chartType) {
    case 'evolution':
      return {
        title: model.labels.timeSeries,
        description: model.labels.timeSeriesHint,
        total: model.timeSeries.at(-1)?.cumulative || 0,
        data: model.timeSeries,
      }
    case 'valeur-par-panier':
      return {
        title: 'Valeur par panier',
        description: 'Montant actuel de chaque panier, du plus élevé au plus faible.',
        total: model.cartValueTotal,
        average: model.averageCartValue,
        data: model.cartRows,
      }
    case 'repartition':
      return {
        title: model.labels.distribution,
        description: 'Part de la valeur actuelle par panier. Les paniers hors du top 5 sont regroupés.',
        total: model.pieData.reduce((sum, item) => sum + item.value, 0),
        data: model.pieData,
      }
    case 'budget-comparison':
      return {
        title: model.labels.budget,
        description: model.labels.utilizationHint,
        totalBudget: model.budgetTotal,
        totalValeur: model.valueWithBudget,
        data: model.budgetComparison,
      }
    case 'evolution-mensuelle':
      return {
        title: model.labels.monthly,
        description: 'Somme des valeurs actuelles des paniers créés durant chaque mois.',
        total: model.monthlyData.reduce((sum, month) => sum + month.total, 0),
        data: model.monthlyData,
      }
    case 'produits-frequents':
      return {
        title: 'Produits les plus fréquents',
        description: 'Nombre de références (lignes), distinct du nombre d’unités.',
        total: model.mostFrequentProducts.reduce((sum, item) => sum + item.count, 0),
        data: model.mostFrequentProducts,
      }
    case 'produits-chers':
      return {
        title: 'Prix unitaires les plus élevés',
        description: 'Classement des lignes selon le prix unitaire actuel, pas un historique d’achat.',
        data: model.topExpensiveProducts,
      }
    case 'distribution-prix':
      return {
        title: 'Distribution des prix unitaires',
        description: 'Répartition des références par tranche de prix unitaire actuel.',
        total: model.priceDistribution.reduce((sum, item) => sum + item.count, 0),
        data: model.priceDistribution,
      }
    case 'details-paniers':
      return {
        title: 'Détails des paniers',
        description: 'Valeurs actuelles. Les paniers sans budget n’ont pas d’écart.',
        total: model.cartValueTotal,
        totalBudget: model.budgetTotal,
        data: model.cartRows,
      }
    default:
      return null
  }
}
